import React, { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaYoutube, FaFacebookF, FaInstagram } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';
import { useData } from '../context/DataContext';
import { useLanguage } from '../context/LanguageContext';

const Main = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  background: ${props => props.themeMode === 'light' 
    ? 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 50%, #f0f4ff 100%)' 
    : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'};
  color: ${props => props.themeMode === 'light' ? '#333' : '#fff'};
  overflow-x: hidden;
`;

const Header = styled.section`
  text-align: center;
  padding: 6rem 2rem 4rem 2rem;
  background: ${props => props.themeMode === 'light' 
    ? 'linear-gradient(135deg, rgba(124, 77, 255, 0.1) 0%, rgba(83, 109, 254, 0.1) 100%)' 
    : 'linear-gradient(135deg, rgba(106, 27, 154, 0.3) 0%, rgba(57, 73, 171, 0.3) 100%)'};
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  background: linear-gradient(90deg, #7c4dff 0%, #536dfe 50%, #b388ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.3rem;
  color: ${props => props.themeMode === 'light' ? '#666666' : '#b39ddb'};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.8;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const InfoCard = styled(motion.div)`
  background: ${props => props.themeMode === 'light' 
    ? '#ffffff' 
    : 'rgba(255, 255, 255, 0.05)'};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.2)' 
    : 'rgba(179, 157, 219, 0.2)'};
  box-shadow: ${props => props.themeMode === 'light' 
    ? '0 4px 20px rgba(0, 0, 0, 0.08)' 
    : 'none'};
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  transition: all 0.3s ease;
  &:hover {
    border-color: #7c4dff;
    transform: translateX(10px);
  }
`;

const IconWrapper = styled.div`
  font-size: 2rem;
  color: #7c4dff;
  min-width: 50px;
  height: 50px;
  background: ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.15)' 
    : 'rgba(124, 77, 255, 0.2)'};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoContent = styled.div``;

const InfoTitle = styled.h3`
  font-size: 1.3rem;
  color: ${props => props.themeMode === 'light' ? '#333333' : '#b39ddb'};
  margin-bottom: 0.5rem;
`;

const InfoText = styled.p`
  color: ${props => props.themeMode === 'light' ? '#666666' : '#a0a0c0'};
  line-height: 1.6;
  font-size: 1rem;
`;

const FormSection = styled(motion.div)`
  background: ${props => props.themeMode === 'light' 
    ? '#ffffff' 
    : 'rgba(255, 255, 255, 0.05)'};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2.5rem;
  border: 1px solid ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.2)' 
    : 'rgba(179, 157, 219, 0.2)'};
  box-shadow: ${props => props.themeMode === 'light' 
    ? '0 4px 20px rgba(0, 0, 0, 0.08)' 
    : 'none'};
`;

const FormTitle = styled.h2`
  font-size: 1.8rem;
  color: ${props => props.themeMode === 'light' ? '#333333' : '#b39ddb'};
  margin-bottom: 2rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: ${props => props.themeMode === 'light' ? '#333333' : '#b39ddb'};
  font-size: 0.95rem;
`;

const Input = styled.input`
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.3)' 
    : 'rgba(179, 157, 219, 0.3)'};
  background: ${props => props.themeMode === 'light' 
    ? '#f8f9ff' 
    : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.themeMode === 'light' ? '#333333' : '#fff'};
  font-size: 1rem;
  transition: all 0.3s ease;
  &:focus {
    outline: none;
    border-color: #7c4dff;
    box-shadow: 0 0 20px rgba(124, 77, 255, 0.3);
  }
  &::placeholder {
    color: ${props => props.themeMode === 'light' ? '#999999' : '#666'};
  }
`;

const TextArea = styled.textarea`
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.3)' 
    : 'rgba(179, 157, 219, 0.3)'};
  background: ${props => props.themeMode === 'light' 
    ? '#f8f9ff' 
    : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.themeMode === 'light' ? '#333333' : '#fff'};
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;
  &:focus {
    outline: none;
    border-color: #7c4dff;
    box-shadow: 0 0 20px rgba(124, 77, 255, 0.3);
  }
  &::placeholder {
    color: ${props => props.themeMode === 'light' ? '#999999' : '#666'};
  }
`;

const SubmitButton = styled(motion.button)`
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #7c4dff 0%, #536dfe 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 8px 30px rgba(124, 77, 255, 0.4);
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(124, 77, 255, 0.6);
  }
`;

const MapSection = styled.section`
  padding: 4rem 2rem;
  background: ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.05)' 
    : 'rgba(0, 0, 0, 0.2)'};
`;

const MapContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: ${props => props.themeMode === 'light' 
    ? '0 10px 40px rgba(0, 0, 0, 0.1)' 
    : '0 20px 60px rgba(0, 0, 0, 0.3)'};
  
  iframe {
    width: 100%;
    height: 450px;
    border: none;
    border-radius: 20px;
    
    @media (max-width: 768px) {
      height: 350px;
    }
    
    @media (max-width: 480px) {
      height: 280px;
    }
  }
`;

const SocialSection = styled.section`
  padding: 4rem 2rem;
  text-align: center;
  background: ${props => props.themeMode === 'light' ? '#f8f9ff' : 'transparent'};
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 2rem;
  background: linear-gradient(90deg, #7c4dff 0%, #536dfe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.1)' 
    : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
  font-size: 2rem;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  &:hover {
    background: linear-gradient(135deg, #7c4dff 0%, #536dfe 100%);
    color: #fff;
    transform: scale(1.1);
    border-color: #b39ddb;
  }
`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

// Scroll animation hook
const useScrollAnimation = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);
  
  return { ref, controls };
};

function Contact() {
  const { theme } = useTheme();
  const { data, addMessage } = useData();
  const { t } = useLanguage();
  const themeMode = theme.mode;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const contentAnimation = useScrollAnimation();
  const mapAnimation = useScrollAnimation();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Add message to data context
    addMessage(formData);
    alert(t.contact.thankYou);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt />,
      title: t.contact.address,
      text: data.churchInfo?.address || 'Davao City, Philippines'
    },
    {
      icon: <FaPhone />,
      title: t.contact.phone,
      text: data.churchInfo?.phone || '+63 XXX XXX XXXX'
    },
    {
      icon: <FaEnvelope />,
      title: t.contact.email,
      text: data.churchInfo?.email || 'info@houseofworshipdavao.com'
    },
    {
      icon: <FaClock />,
      title: t.contact.serviceHours,
      text: 'Sunday: 9:00 AM - 12:00 PM\nWednesday: 7:00 PM - 9:00 PM'
    }
  ];

  return (
    <Main themeMode={themeMode}>
      <Header themeMode={themeMode}>
        <Title
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {t.contact.title}
        </Title>
        <Subtitle
          themeMode={themeMode}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {t.contact.subtitle}
        </Subtitle>
      </Header>

      <ContentWrapper>
        <motion.div
          ref={contentAnimation.ref}
          initial="hidden"
          animate={contentAnimation.controls}
          variants={staggerContainer}
        >
          <ContactInfo>
            {contactInfo.map((info, index) => (
              <InfoCard key={index} themeMode={themeMode} variants={fadeInLeft}>
                <IconWrapper themeMode={themeMode}>{info.icon}</IconWrapper>
                <InfoContent>
                  <InfoTitle themeMode={themeMode}>{info.title}</InfoTitle>
                  <InfoText themeMode={themeMode} style={{ whiteSpace: 'pre-line' }}>{info.text}</InfoText>
                </InfoContent>
              </InfoCard>
            ))}
          </ContactInfo>
        </motion.div>

        <FormSection
          themeMode={themeMode}
          initial={{ opacity: 0, x: 60 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <FormTitle themeMode={themeMode}>{t.contact.sendMessage}</FormTitle>
          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <Label themeMode={themeMode}>{t.contact.yourName}</Label>
              <Input
                themeMode={themeMode}
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t.contact.yourName}
                required
              />
            </InputGroup>
            <InputGroup>
              <Label themeMode={themeMode}>{t.contact.emailAddress}</Label>
              <Input
                themeMode={themeMode}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t.contact.emailAddress}
                required
              />
            </InputGroup>
            <InputGroup>
              <Label themeMode={themeMode}>{t.contact.subject}</Label>
              <Input
                themeMode={themeMode}
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder={t.contact.subject}
                required
              />
            </InputGroup>
            <InputGroup>
              <Label themeMode={themeMode}>{t.contact.message}</Label>
              <TextArea
                themeMode={themeMode}
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={t.contact.message}
                required
              />
            </InputGroup>
            <SubmitButton
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t.contact.send}
            </SubmitButton>
          </Form>
        </FormSection>
      </ContentWrapper>

      {/* Google Map */}
      <MapSection themeMode={themeMode}>
        <motion.div
          ref={mapAnimation.ref}
          initial="hidden"
          animate={mapAnimation.controls}
          variants={fadeInUp}
        >
          <SectionTitle>📍 {t.contact.findUs}</SectionTitle>
          <MapContainer themeMode={themeMode}>
            <iframe
              title="Church Location"
              src={data.churchInfo?.mapEmbed || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d989.8909901885514!2d125.55401911959839!3d7.060409100000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x32f90d0046b2a34d%3A0xd513d93b104d9c75!2sHouse%20of%20Worship%20(HOW)!5e0!3m2!1sen!2sph!4v1735100000000!5m2!1sen!2sph"}
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </MapContainer>
        </motion.div>
      </MapSection>

      {/* Social Media */}
      <SocialSection themeMode={themeMode}>
        <SectionTitle
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Connect With Us
        </SectionTitle>
        <SocialLinks>
          <SocialLink
            themeMode={themeMode}
            href="https://youtube.com"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.2, rotate: 5 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaYoutube />
          </SocialLink>
          <SocialLink
            themeMode={themeMode}
            href="https://facebook.com"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.2, rotate: -5 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaFacebookF />
          </SocialLink>
          <SocialLink
            themeMode={themeMode}
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.2, rotate: 5 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaInstagram />
          </SocialLink>
        </SocialLinks>
      </SocialSection>
    </Main>
  );
}

export default Contact;
